import { useEffect, useState } from "react";
import { FileText, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

/**
 * AuthImage — fetches a file from /api/files/{id}/download as a blob
 * (using axios + Bearer auth) and renders it as an <img>.
 * Falls back to a PDF icon for non-image content types.
 *
 * Props:
 *  - fileId: string (required)
 *  - contentType: string (optional hint — skips fetch for non-images)
 *  - alt: string
 *  - className: string for the rendered <img> / icon container
 *  - testId: data-testid (optional)
 */
export default function AuthImage({ fileId, contentType, alt = "", className = "", testId }) {
  const [url, setUrl] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error | non-image

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    const isImage = contentType ? contentType.startsWith("image/") : true;
    if (!isImage) {
      setState("non-image");
      return () => {};
    }

    (async () => {
      try {
        const { data } = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
        if (!active) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
        setState("ok");
      } catch {
        if (active) setState("error");
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, contentType]);

  if (state === "loading") {
    return (
      <div className={`flex items-center justify-center bg-[#F9F6F0] text-ink-muted ${className}`} data-testid={testId}>
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (state === "non-image") {
    return (
      <div className={`flex items-center justify-center bg-[#F9F6F0] text-[#5B2C82] ${className}`} data-testid={testId}>
        <FileText className="h-5 w-5" />
      </div>
    );
  }
  if (state === "error") {
    return (
      <div className={`flex items-center justify-center bg-rose-50 text-rose-700 ${className}`} data-testid={testId}>
        <AlertTriangle className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt}
      className={`object-cover ${className}`}
      data-testid={testId}
    />
  );
}
